import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { AIBrainLoader } from '../components/ui/ai-loading';
import {
  Activity, Brain, HeartPulse, Plus, Loader2, AlertTriangle, ShieldCheck,
  Stethoscope, ClipboardList, TrendingUp, Gauge, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK = {
  high: { label: 'High risk', cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
  moderate: { label: 'Moderate', cls: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  low: { label: 'Low risk', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
};

const RiskPill = ({ level, testId }) => {
  const r = RISK[level] || { label: 'No data', cls: 'text-cn-text-muted bg-cn-soft border-cn-border', dot: 'bg-cn-text-muted' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${r.cls}`} data-testid={testId}>
      <span className={`w-1.5 h-1.5 rounded-full ${r.dot}`} /> {r.label}
    </span>
  );
};

const CONDITIONS = ['CHF', 'COPD', 'Post-surgical', 'Diabetes', 'Stroke recovery', 'Wound care', 'Dementia', 'Other'];

const VITAL_FIELDS = [
  { k: 'systolic', label: 'Systolic', ph: '120' },
  { k: 'diastolic', label: 'Diastolic', ph: '80' },
  { k: 'heart_rate', label: 'Heart rate', ph: '72' },
  { k: 'spo2', label: 'SpO₂ %', ph: '98' },
  { k: 'temperature_f', label: 'Temp °F', ph: '98.6' },
  { k: 'weight_lbs', label: 'Weight lbs', ph: '180' },
  { k: 'blood_glucose', label: 'Glucose', ph: '110' },
  { k: 'pain_level', label: 'Pain 0–10', ph: '2' },
];

export default function ClinicianRiskMonitor() {
  const { getAuthHeader, token } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ patient_initials: '', primary_diagnosis: '', condition_focus: '' });
  const [vitals, setVitals] = useState({});
  const [logging, setLogging] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clinician/care-patients`, getAuthHeader());
      setPatients(data);
    } catch (e) { toast.error('Could not load patients'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  const loadDetail = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/clinician/care-patients/${id}`, getAuthHeader());
      setDetail(data);
    } catch (e) { toast.error('Could not load patient'); }
  }, [getAuthHeader]);

  useEffect(() => { if (token) loadList(); }, [token, loadList]);
  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  const addPatient = async () => {
    if (!addForm.patient_initials.trim()) { toast.error('Enter patient initials'); return; }
    try {
      const { data } = await axios.post(`${API}/clinician/care-patients`, addForm, getAuthHeader());
      toast.success('Patient added to monitoring');
      setShowAdd(false);
      setAddForm({ patient_initials: '', primary_diagnosis: '', condition_focus: '' });
      await loadList();
      setSelected(data.id);
    } catch (e) { toast.error('Could not add patient'); }
  };

  const logVitals = async () => {
    const payload = {};
    Object.entries(vitals).forEach(([k, v]) => {
      if (v === '' || v == null) return;
      payload[k] = ['mobility', 'med_adherence', 'behavioral_notes'].includes(k) ? v : Number(v);
    });
    if (Object.keys(payload).length === 0) { toast.error('Enter at least one vital'); return; }
    setLogging(true);
    try {
      const { data } = await axios.post(`${API}/clinician/care-patients/${selected}/vitals`, payload, getAuthHeader());
      const lvl = data.risk.level;
      toast[lvl === 'high' ? 'error' : lvl === 'moderate' ? 'warning' : 'success'](
        `Risk: ${RISK[lvl].label} — ${data.risk.flags.length} flag(s)`
      );
      setVitals({});
      await Promise.all([loadDetail(selected), loadList()]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not log vitals'); }
    finally { setLogging(false); }
  };

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard' },
    { icon: Brain, label: 'Risk Monitor', active: true, highlight: true },
    { icon: ClipboardList, label: 'Clinical Notes', href: '/clinician/notes' },
    { icon: Stethoscope, label: 'Care Requests', href: '/clinician/care-requests' },
  ];

  const setV = (k) => (e) => setVitals((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  return (
    <DashboardShell
      navItems={navItems}
      title="Predictive Risk Monitor"
      subtitle="Log daily vitals — CaraLoom's AI flags early warning signs to prevent readmissions."
      testId="risk-monitor-page"
    >
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Patient list */}
        <div className="lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-cn-text">Patients under care</p>
            <button onClick={() => setShowAdd((s) => !s)} className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-sm" data-testid="add-patient-toggle">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>

          {showAdd && (
            <div className="cn-card rounded-2xl p-4 mb-3 space-y-3" data-testid="add-patient-form">
              <div>
                <Label className="text-xs">Patient initials *</Label>
                <Input value={addForm.patient_initials} onChange={(e) => setAddForm((f) => ({ ...f, patient_initials: e.target.value }))} placeholder="E.W." data-testid="add-patient-initials" />
              </div>
              <div>
                <Label className="text-xs">Diagnosis</Label>
                <Input value={addForm.primary_diagnosis} onChange={(e) => setAddForm((f) => ({ ...f, primary_diagnosis: e.target.value }))} placeholder="CHF exacerbation" data-testid="add-patient-diagnosis" />
              </div>
              <div>
                <Label className="text-xs">Condition focus</Label>
                <Select value={addForm.condition_focus} onValueChange={(v) => setAddForm((f) => ({ ...f, condition_focus: v }))}>
                  <SelectTrigger data-testid="add-patient-condition"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <button onClick={addPatient} className="cn-btn cn-btn-primary w-full justify-center !py-2 text-sm" data-testid="add-patient-submit">Start monitoring</button>
            </div>
          )}

          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary" /></div>
          ) : patients.length === 0 ? (
            <div className="cn-card rounded-2xl p-8 text-center" data-testid="patients-empty">
              <HeartPulse className="w-10 h-10 text-cn-primary mx-auto mb-2" />
              <p className="text-sm text-cn-text font-medium">No patients monitored yet</p>
              <p className="text-xs text-cn-text-muted mt-1">Add a patient to start tracking vitals and early-warning risk.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="patients-list">
              {patients.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelected(p.id)}
                  className={`w-full text-left cn-card rounded-2xl p-4 transition-all ${selected === p.id ? 'ring-2 ring-cn-primary' : 'hover:border-cn-primary/40'}`}
                  data-testid={`patient-card-${p.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-cn-text truncate">{p.patient_initials}</p>
                      <p className="text-xs text-cn-text-muted truncate">{p.condition_focus || p.primary_diagnosis || 'Home care'}</p>
                    </div>
                    <RiskPill level={p.latest_risk?.level} testId={`patient-risk-${p.id}`} />
                  </div>
                  <p className="text-[11px] text-cn-text-muted mt-1.5">{p.vitals_count || 0} vitals logged</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="cn-card rounded-2xl p-12 text-center h-full flex flex-col items-center justify-center" data-testid="detail-empty">
              <Gauge className="w-12 h-12 text-cn-primary mb-3" />
              <p className="text-cn-text font-medium">Select a patient to log vitals & view risk</p>
            </div>
          ) : !detail ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
          ) : (
            <div className="space-y-5" data-testid="patient-detail">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="cn-display text-xl text-cn-text">{detail.patient.patient_initials}</h2>
                  <p className="text-sm text-cn-text-muted">{detail.patient.primary_diagnosis || detail.patient.condition_focus || 'Home care'}</p>
                </div>
                <RiskPill level={detail.latest_risk?.level} testId="detail-risk-pill" />
              </div>

              {/* Latest AI risk assessment */}
              {logging ? (
                <div className="rounded-2xl border border-cn-border bg-cn-soft/40 dark:bg-cn-ink-800" data-testid="risk-assessing">
                  <AIBrainLoader message="Running the risk engine on the latest vitals..." size={72} />
                </div>
              ) : detail.latest_risk ? (
                <div className={`rounded-2xl border p-5 ${RISK[detail.latest_risk.level]?.cls || 'border-cn-border'}`} data-testid="risk-assessment">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4" />
                    <p className="text-sm font-bold">AI risk assessment · score {detail.latest_risk.score}/100</p>
                  </div>
                  <p className="text-sm text-cn-text mb-3" data-testid="risk-summary">{detail.latest_risk.summary}</p>
                  {detail.latest_risk.flags?.length > 0 && (
                    <div className="space-y-1.5 mb-3">
                      {detail.latest_risk.flags.map((f, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs" data-testid={`risk-flag-${i}`}>
                          <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${f.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                          <span><span className="font-semibold">{f.category}:</span> {f.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-start gap-2 text-xs bg-white/60 dark:bg-black/20 rounded-lg p-2.5" data-testid="risk-action">
                    <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-cn-primary" />
                    <span><span className="font-semibold">Recommended:</span> {detail.latest_risk.recommended_action}</span>
                  </div>
                </div>
              ) : (
                <div className="cn-card rounded-2xl p-5 text-sm text-cn-text-muted" data-testid="no-risk-yet">
                  Log the first set of vitals to generate an AI risk assessment.
                </div>
              )}

              {/* Log vitals */}
              <div className="cn-card rounded-2xl p-5" data-testid="log-vitals-form">
                <p className="text-sm font-bold text-cn-text mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cn-primary" /> Log today's vitals</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {VITAL_FIELDS.map((f) => (
                    <div key={f.k}>
                      <Label className="text-[11px]">{f.label}</Label>
                      <Input type="number" value={vitals[f.k] ?? ''} onChange={setV(f.k)} placeholder={f.ph} data-testid={`vital-${f.k}`} />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-[11px]">Mobility</Label>
                    <Select value={vitals.mobility || ''} onValueChange={setV('mobility')}>
                      <SelectTrigger data-testid="vital-mobility"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="improving">Improving</SelectItem>
                        <SelectItem value="stable">Stable</SelectItem>
                        <SelectItem value="declining">Declining</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px]">Medication adherence</Label>
                    <Select value={vitals.med_adherence || ''} onValueChange={setV('med_adherence')}>
                      <SelectTrigger data-testid="vital-med-adherence"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">Full</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="missed">Missed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-[11px]">Behavioral / observation notes</Label>
                  <Input value={vitals.behavioral_notes || ''} onChange={setV('behavioral_notes')} placeholder="e.g. short of breath climbing stairs" data-testid="vital-notes" />
                </div>
                <button onClick={logVitals} disabled={logging} className="cn-btn cn-btn-primary w-full justify-center !py-2.5 mt-4" data-testid="log-vitals-submit">
                  {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                  Log vitals & assess risk
                </button>
              </div>

              {/* History */}
              {detail.vitals?.length > 0 && (
                <div className="cn-card rounded-2xl p-5" data-testid="vitals-history">
                  <p className="text-sm font-bold text-cn-text mb-3">Recent vitals</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-cn-text-muted text-left">
                        <tr>
                          <th className="py-1 pr-3 font-medium">Date</th>
                          <th className="py-1 pr-3 font-medium">BP</th>
                          <th className="py-1 pr-3 font-medium">HR</th>
                          <th className="py-1 pr-3 font-medium">SpO₂</th>
                          <th className="py-1 pr-3 font-medium">Temp</th>
                          <th className="py-1 pr-3 font-medium">Wt</th>
                        </tr>
                      </thead>
                      <tbody className="text-cn-text">
                        {detail.vitals.slice(0, 10).map((v) => (
                          <tr key={v.id} className="border-t border-cn-border/60">
                            <td className="py-1.5 pr-3">{new Date(v.recorded_at).toLocaleDateString()}</td>
                            <td className="py-1.5 pr-3">{v.systolic ? `${v.systolic}/${v.diastolic ?? '—'}` : '—'}</td>
                            <td className="py-1.5 pr-3">{v.heart_rate ?? '—'}</td>
                            <td className="py-1.5 pr-3">{v.spo2 ? `${v.spo2}%` : '—'}</td>
                            <td className="py-1.5 pr-3">{v.temperature_f ?? '—'}</td>
                            <td className="py-1.5 pr-3">{v.weight_lbs ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
